import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa";

const ForgotPasswordPage = () => {
  const { id, token } = useParams();
  const navigate = useNavigate();

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    newPassword: "",
    confirmPassword: "",
  });
  const [error, setError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  // Password validation regex
  const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])[a-zA-Z0-9!@#$%^&*]{8,16}$/;

  const validatePassword = (password) => {
    if (!password) {
      return "Password is required";
    }
    if (password.length < 8) {
      return "Password must be at least 8 characters";
    }
    if (!passwordRegex.test(password)) {
      return "Password must contain uppercase, lowercase, number, and special character";
    }
    return "";
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === "newPassword") {
      setPasswordError("");
    }
    if (name === "confirmPassword") {
      setError("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setPasswordError("");
    const passwordValidationError = validatePassword(formData.newPassword);
    if (passwordValidationError) {
      setPasswordError(passwordValidationError);
      return;
    }

    // Check if passwords match
    if (formData.newPassword !== formData.confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/password/forgot-password/${id}/${token}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            new_password: formData.newPassword,
          }),
        }
      );

      const data = await response.json();

      if (response.ok && data.isLoggedIn === true) {
        setIsSuccess(true);
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate("/log-in");
        }, 3000);
      } else {
        setError(data.message || "Failed to reset password. Please try again.");
      }
    } catch (err) {
      console.error("Reset password error:", err);
      setError("Server error. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="page-bg">
          <div className="create-survey-logo">
            <Link to="/">
              <img
                src="/json-media/img/logo.svg"
                alt="GI"
                width={50}
                style={{ padding: 5 }}
              />
            </Link>
          </div>
          <div className="content-center">
          <div className="signup-container">
            <div className="signup-header">
              <h3>Password Reset Successful!</h3>
              <p style={{ color: "#10b981" }}>
                Your password has been updated successfully. Redirecting to login...
              </p>
            </div>
            <div style={{ textAlign: "center", marginTop: "20px" }}>
              <Link to="/log-in" style={{ color: "rgb(59, 130, 246)" }}>
                Go to Login
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-bg">
      <div className="create-survey-logo">
        <Link to="/">
          <img
            src="/json-media/img/logo.svg"
            alt="GI"
            width={50}
            style={{ padding: 5 }}
          />
        </Link>
      </div>

      <div className="content-center">
        <div className="signup-container">
          <div className="signup-header">
            <h3>Reset Password</h3>
            <p>Create a new password for your account</p>
          </div>
          <form className="signup-form" onSubmit={handleSubmit} noValidate>
            {/* New Password */}
            <div className="form-group password-group">
              <label>New Password</label>
              <div className="password-wrapper">
                <input
                  type={showPassword ? "text" : "password"}
                  name="newPassword"
                  placeholder="Enter new password"
                  value={formData.newPassword}
                  onChange={handleChange}
                  disabled={isLoading}
                />
                <span
                  className="toggle-eye"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <FaEye /> : <FaEyeSlash />}
                </span>
              </div>
              {passwordError && <span className="error-text">{passwordError}</span>}
              <small style={{ color: "#6b7280", fontSize: "0.75rem", marginTop: "4px", display: "block" }}>
                Must contain 8-16 characters with uppercase, lowercase, number, and special character (!@#$%^&*)
              </small>
            </div>
            {/* Confirm Password */}
            <div className="form-group password-group">
              <label>Confirm Password</label>
              <div className="password-wrapper">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  name="confirmPassword"
                  placeholder="Confirm new password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  disabled={isLoading}
                />
                <span
                  className="toggle-eye"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <FaEye /> : <FaEyeSlash />}
                </span>
              </div>
              {error && <span className="error-text">{error}</span>}
            </div>
            <button
              type="submit"
              className="signup-btn"
              disabled={isLoading || !formData.newPassword || !formData.confirmPassword}
            >
              {isLoading ? "Resetting..." : "Reset Password"}
            </button>
          </form>
          <div style={{ marginTop: "20px", textAlign: "center" }}>
            <Link to="/log-in" style={{ color: "rgb(59, 130, 246)" }}>
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;